(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (n, min, max) => Math.max(min, Math.min(n, max));
  const controllers = new Map();

  const markMissingImage = (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) return;
    if (!image.matches('.oc-card__image, .product__media img, .card__media img')) return;
    image.classList.add('oc-image-missing');
  };
  window.addEventListener('error', markMissingImage, true);

  class OcCarousel extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.track = this.querySelector('.oc-carousel__track');
      if (!this.track) return;
      this.events = new AbortController();
      const on = (node, event, fn) => node?.addEventListener(event, fn, { signal: this.events.signal });
      this.progress = this.querySelector('.oc-carousel__progress');
      this.pagination = this.querySelector('[data-oc-pagination]');
      on(this.track, 'scroll', () => this.update());
      on(this.track, 'pointerdown', () => this.stop());
      on(this.track, 'wheel', () => this.stop());
      on(this.querySelector('[data-oc-prev]'), 'click', () => this.move(-1));
      on(this.querySelector('[data-oc-next]'), 'click', () => this.move(1));
      on(this.track, 'keydown', (e) => {
        if (e.target !== this.track) return;
        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) { e.preventDefault(); this.move(e.key === 'ArrowLeft' ? -1 : 1); }
      });
      on(this.progress, 'keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        if (e.key === 'Home' || e.key === 'End') this.to(e.key === 'Home' ? 0 : this.max());
        else this.move(e.key === 'ArrowLeft' ? -1 : 1);
      });
      on(this.progress, 'pointerdown', (e) => {
        this.progress.setPointerCapture(e.pointerId);
        this.dragging = true;
        this.seek(e);
      });
      on(this.progress, 'pointermove', (e) => { if (this.dragging) this.seek(e); });
      on(this.progress, 'pointerup', () => { this.dragging = false; });
      on(this.progress, 'pointercancel', () => { this.dragging = false; });
      if (this.pagination) {
        this.pagination.replaceChildren();
        [...this.track.children].forEach((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', `Go to slide ${index + 1}`);
          button.setAttribute('aria-controls', this.track.id);
          on(button, 'click', () => this.to(slide.offsetLeft - this.track.firstElementChild.offsetLeft));
          this.pagination.append(button);
        });
        this.pagination.hidden = this.track.children.length < 2;
      }
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.update();
    }
    max() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
    move(direction) {
      const first = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.to(this.track.scrollLeft + direction * ((first?.getBoundingClientRect().width || 0) + gap));
    }
    stop() {
      cancelAnimationFrame(this.frame);
      this.track?.style.removeProperty('scroll-snap-type');
    }
    to(position) {
      this.stop();
      const target = clamp(position, 0, this.max());
      if (reduced()) { this.track.scrollLeft = target; return; }
      const start = this.track.scrollLeft;
      const duration = Number(this.dataset.speed) || 300;
      const began = performance.now();
      this.track.style.scrollSnapType = 'none';
      const tick = (now) => {
        const t = clamp((now - began) / duration, 0, 1);
        this.track.scrollLeft = start + (target - start) * (1 - Math.pow(1 - t, 3));
        if (t < 1) this.frame = requestAnimationFrame(tick);
        else this.track.style.removeProperty('scroll-snap-type');
      };
      this.frame = requestAnimationFrame(tick);
    }
    seek(e) {
      const box = this.progress.getBoundingClientRect();
      const ratio = clamp((e.clientX - box.left) / box.width, 0, 1);
      this.to(ratio * this.max());
    }
    update() {
      const max = this.max();
      const value = max ? clamp(this.track.scrollLeft / max, 0, 1) : 0;
      const prev = this.querySelector('[data-oc-prev]');
      const next = this.querySelector('[data-oc-next]');
      if (prev) prev.disabled = value < .001;
      if (next) next.disabled = !max || value > .999;
      if (this.progress) {
        this.progress.hidden = !max;
        this.progress.setAttribute('aria-valuenow', Math.round(value * 100));
        const width = this.track.clientWidth / this.track.scrollWidth * 100;
        this.progress.style.setProperty('--oc-progress-width', `${width}%`);
        this.progress.firstElementChild.style.left = `${value * (100 - width)}%`;
      }
      if (this.pagination) {
        const active = Math.round(this.track.scrollLeft / this.track.clientWidth);
        [...this.pagination.children].forEach((button, index) => button.setAttribute('aria-current', String(index === active)));
      }
    }
    disconnect() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.resize?.disconnect();
      this.track?.style.removeProperty('scroll-snap-type');
    }
    disconnectedCallback() { this.disconnect(); }
  }
  if (!customElements.get('oc-carousel')) customElements.define('oc-carousel', OcCarousel);

  function setupPredictiveSearch(header, events, cleanups) {
    const input = header.querySelector('.oc-search input[name="q"]');
    const results = header.querySelector('[data-oc-search-results]');
    if (!input || !results) return;
    let controller;
    let sequence = 0;
    const clear = () => { results.replaceChildren(); results.hidden = true; };
    const render = (products) => {
      results.replaceChildren();
      if (!products.length) { results.hidden = true; return; }
      const list = document.createElement('ul');
      list.className = 'oc-search-results__list';
      products.slice(0, 4).forEach((product, index) => {
        const item = document.createElement('li');
        item.className = 'oc-search-results__item';
        item.style.animationDelay = `${index * 100}ms`;
        const link = document.createElement('a');
        link.href = product.url || '#';
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.title || '';
          image.loading = 'lazy';
          link.append(image);
        }
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title || '';
        copy.append(title);
        if (product.price != null) {
          const price = document.createElement('span');
          price.textContent = String(product.price);
          copy.append(price);
        }
        link.append(copy);
        item.append(link);
        list.append(item);
      });
      results.append(list);
      results.hidden = false;
    };
    const search = async () => {
      const value = input.value.trim();
      controller?.abort();
      if (value.length < 2) { clear(); return; }
      const requestId = ++sequence;
      controller = new AbortController();
      const action = new URL(input.form?.action || '/search', window.location.href);
      action.pathname = action.pathname.replace(/\/search\/?$/, '/search/suggest.json');
      action.search = '';
      action.searchParams.set('q', value);
      action.searchParams.set('resources[type]', 'product');
      action.searchParams.set('resources[limit]', '4');
      action.searchParams.set('resources[options][unavailable_products]', 'hide');
      try {
        const response = await fetch(action, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
        const data = await response.json();
        if (requestId !== sequence) return;
        render(data.resources?.results?.products || []);
      } catch (error) {
        if (error.name !== 'AbortError') clear();
      }
    };
    input.addEventListener('input', search, { signal: events.signal });
    input.addEventListener('focus', search, { signal: events.signal });
    cleanups.push(() => controller?.abort());
  }

  function setupRails(root, events) {
    const rails = [root, ...root.querySelectorAll('.oc-product-rail')].filter((rail) => rail.matches?.('.oc-product-rail'));
    rails.forEach((rail) => {
      const tabs = [...rail.querySelectorAll('[data-oc-rail-tab]')];
      const panels = [...rail.querySelectorAll('[data-oc-rail-panel]')];
      if (!tabs.length || !panels.length || rail.dataset.ocRailsReady) return;
      rail.dataset.ocRailsReady = 'true';
      const activate = (tab) => {
        const key = tab.dataset.ocRailTab;
        tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.dataset.ocRailPanel !== key; });
        rail.querySelector(`[data-oc-rail-panel="${CSS.escape(key)}"] oc-carousel`)?.update();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab), { signal: events.signal });
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          tabs[next].focus();
          activate(tabs[next]);
        }, { signal: events.signal });
      });
    });
  }

  function setupAccordions(scope, events) {
    scope.querySelectorAll('.oc-accordion').forEach((details) => {
      if (details.dataset.ocAccordionReady) return;
      const summary = details.querySelector(':scope > summary');
      const content = details.querySelector(':scope > .rte, :scope > .oc-product-faq__answer');
      if (!summary || !content) return;
      details.dataset.ocAccordionReady = 'true';
      content.style.overflow = 'hidden';
      const immediate = () => { details.classList.remove('oc-closing'); content.style.height = details.open ? 'auto' : '0px'; };
      immediate();
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const opening = !details.open;
        if (reduced()) { details.open = opening; immediate(); return; }
        content.style.transition = 'height 300ms ease-out';
        if (opening) {
          details.classList.remove('oc-closing');
          details.open = true;
          content.style.height = '0px';
          content.getBoundingClientRect();
          content.style.height = `${content.scrollHeight}px`;
          content.addEventListener('transitionend', () => { if (details.open) content.style.height = 'auto'; }, { once: true, signal: events.signal });
        } else {
          content.style.height = `${content.scrollHeight}px`;
          content.getBoundingClientRect();
          details.classList.add('oc-closing');
          details.open = false;
          content.style.height = '0px';
          content.addEventListener('transitionend', () => { details.classList.remove('oc-closing'); }, { once: true, signal: events.signal });
        }
      }, { signal: events.signal });
    });
  }

  function setupHeader(header, events, cleanups) {
    const on = (node, type, fn) => node?.addEventListener(type, fn, { signal: events.signal });
    header.closest('.shopify-section')?.classList.add('oc-header-section');
    const measure = () => document.documentElement.style.setProperty('--oc-header-height', `${header.getBoundingClientRect().height}px`);
    const resize = new ResizeObserver(measure);
    resize.observe(header);
    cleanups.push(() => resize.disconnect());
    measure();
    const updateScroll = () => header.classList.toggle('oc-header-scrolled', window.scrollY > 8);
    window.addEventListener('scroll', updateScroll, { signal: events.signal, passive: true });
    updateScroll();
    setupPredictiveSearch(header, events, cleanups);
    let announcementTimer;
    cleanups.push(() => clearTimeout(announcementTimer));
    on(header.querySelector('[data-oc-announcement-close]'), 'click', () => {
      const announcement = header.querySelector('.oc-announcement');
      if (reduced()) { announcement.hidden = true; measure(); return; }
      announcement.style.height = `${announcement.getBoundingClientRect().height}px`;
      announcement.style.minHeight = '0';
      announcement.getBoundingClientRect();
      announcement.classList.add('oc-closing');
      announcementTimer = setTimeout(() => { announcement.hidden = true; measure(); }, 500);
    });
    const panels = [...header.querySelectorAll('.oc-menu, .oc-search, .oc-header__dropdown')];
    let current;
    let timer;
    const unlock = () => { document.body.classList.remove('oc-panel-open'); };
    cleanups.push(() => { clearTimeout(timer); unlock(); });
    const close = (details, immediate = false) => {
      if (!details?.open) return;
      const summary = details.querySelector(':scope > summary');
      details.classList.add('oc-closing');
      summary.setAttribute('aria-expanded', 'false');
      const finish = () => {
        details.open = false;
        details.classList.remove('oc-closing', 'oc-opening');
        if (current === details) { current = null; unlock(); summary.focus({ preventScroll: true }); }
      };
      clearTimeout(timer);
      if (immediate || reduced()) finish();
      else timer = setTimeout(finish, details.matches('.oc-search') ? 500 : 300);
    };
    panels.forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      const panel = details.querySelector('[role=dialog], .oc-header__dropdown-panel');
      summary.setAttribute('aria-expanded', String(details.open));
      if (panel?.matches('[role=dialog]')) panel.setAttribute('aria-modal', 'true');
      on(summary, 'click', (e) => {
        e.preventDefault();
        if (details.open) { close(details); return; }
        if (current) close(current, true);
        details.classList.add('oc-opening');
        details.open = true;
        current = details;
        summary.setAttribute('aria-expanded', 'true');
        if (!details.matches('.oc-header__dropdown')) document.body.classList.add('oc-panel-open');
        panel?.getBoundingClientRect();
        details.classList.remove('oc-opening');
        (panel?.querySelector('input[name=q]') || panel?.querySelector('input, button, a'))?.focus({ preventScroll: true });
      });
      details.querySelectorAll('[data-oc-menu-close], [data-oc-search-close]').forEach((button) => on(button, 'click', () => close(details)));
    });
    on(document, 'pointerdown', (e) => { if (current && !current.contains(e.target)) close(current); });
    on(document, 'keydown', (e) => {
      if (!current || !current.open) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(current); }
      if (e.key !== 'Tab' || current.matches('.oc-header__dropdown')) return;
      const panel = current.querySelector('[role=dialog]');
      const nodes = [...panel.querySelectorAll('a[href], button:not([disabled]), input, select, summary, [tabindex="0"]')].filter((node) => node.getClientRects().length);
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    });
    const desktop = matchMedia('(min-width:1160px)');
    on(desktop, 'change', () => { if (current) close(current, true); });
  }

  function setupProduct(root, events, cleanups) {
    const on = (node, type, fn) => node?.addEventListener(type, fn, { signal: events.signal });
    root.classList.add('oc-product-ready');
    const expandButton = root.querySelector('[data-oc-gallery-expand]');
    if (expandButton) root.querySelector('media-gallery')?.append(expandButton);
    const sticky = document.createElement('div');
    sticky.className = 'oc-sticky-buy';
    const price = document.createElement('span');
    price.className = 'oc-sticky-buy__price';
    const button = document.createElement('button');
    button.className = 'oc-button oc-button--accent';
    button.type = 'button';
    sticky.append(price, button);
    root.append(sticky);
    let formButton;
    let list;
    let dots;
    let observer;
    let ctaObserver;
    let ctaTarget;
    let ctaOutOfView = false;
    const visible = () => {
      const show = !!formButton && ctaOutOfView && !matchMedia('(min-width:768px)').matches;
      sticky.classList.toggle('oc-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
      button.tabIndex = show ? 0 : -1;
    };
    const syncButton = () => {
      formButton = root.querySelector('.product-form__submit');
      observeCta();
      button.textContent = formButton?.querySelector('span')?.textContent?.trim() || formButton?.textContent?.trim() || '';
      button.disabled = !formButton || formButton.disabled || formButton.getAttribute('aria-disabled') === 'true';
      const plan = root.querySelector('[name=selling_plan]');
      const missingPlan = plan?.required && !plan.value;
      if (missingPlan && formButton && !formButton.disabled) formButton.disabled = true;
      button.disabled = button.disabled || !!missingPlan;
      price.textContent = plan?.selectedOptions[0]?.dataset.price || root.querySelector('.price:not(.price--on-sale) .price-item--regular, .price--on-sale .price-item--sale')?.textContent?.trim() || '';
      visible();
    };
    const observeCta = () => {
      const nextTarget = root.querySelector('.product-form__buttons');
      if (nextTarget === ctaTarget) return;
      ctaObserver?.disconnect();
      ctaTarget = nextTarget;
      ctaOutOfView = false;
      if (ctaTarget) {
        ctaObserver = new IntersectionObserver(([entry]) => {
          ctaOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          visible();
        }, { threshold: 0 });
        ctaObserver.observe(ctaTarget);
      }
      visible();
    };
    on(root, 'change', (e) => { if (e.target.name === 'selling_plan') syncButton(); });
    if (typeof subscribe === 'function') {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== root.dataset.section) return;
        const oldOptions = root.querySelector('.oc-selling-plan');
        const newOptions = data.html.querySelector('.oc-selling-plan');
        if (oldOptions && newOptions) {
          const selected = oldOptions.querySelector('select').value;
          oldOptions.replaceWith(newOptions);
          const select = newOptions.querySelector('select');
          if ([...select.options].some((option) => option.value === selected)) select.value = selected;
        }
        syncButton();
      });
      cleanups.push(unsubscribe);
    }
    on(button, 'click', () => { if (!button.disabled) formButton?.click(); });
    on(window, 'scroll', visible);
    on(window, 'resize', visible);
    const gallery = () => {
      const next = root.querySelector('.product__media-list');
      if (next === list) return;
      observer?.disconnect();
      dots?.remove();
      list = next;
      if (!list) return;
      dots = document.createElement('div');
      dots.className = 'oc-carousel__pagination oc-media-dots';
      dots.setAttribute('aria-label', 'Product media');
      root.querySelector('media-gallery')?.append(dots);
      let mediaKey;
      const update = () => {
        const items = [...list.querySelectorAll(':scope > [data-media-id]')].filter((item) => !item.classList.contains('product__media-item--variant'));
        const key = items.map((item) => item.dataset.mediaId).join(',');
        if (mediaKey !== key) {
          mediaKey = key;
          dots.replaceChildren();
          items.forEach((item, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', `View media ${index + 1}`);
            dot.setAttribute('aria-controls', list.id);
            dot.addEventListener('click', () => {
              root.querySelector('media-gallery')?.setActiveMedia?.(item.dataset.mediaId, false);
              list.scrollTo({ left: item.offsetLeft - items[0].offsetLeft, behavior: reduced() ? 'instant' : 'smooth' });
            }, { signal: events.signal });
            dots.append(dot);
          });
        }
        dots.hidden = items.length < 2;
        const active = Math.round(list.scrollLeft / list.clientWidth);
        [...dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
      };
      on(list, 'scroll', update);
      observer = new MutationObserver(update);
      observer.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-media-id'] });
      update();
    };
    on(root, 'click', (e) => {
      const expand = e.target.closest('[data-oc-gallery-expand]');
      if (!expand) return;
      const expanded = expand.getAttribute('aria-expanded') !== 'true';
      root.classList.toggle('oc-gallery-expanded', expanded);
      expand.setAttribute('aria-expanded', String(expanded));
      expand.querySelector('[data-oc-expand]').hidden = expanded;
      expand.querySelector('[data-oc-collapse]').hidden = !expanded;
    });
    const changes = new MutationObserver((records) => {
      if (records.every((record) => sticky.contains(record.target) || dots?.contains(record.target))) return;
      gallery();
      syncButton();
    });
    changes.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    gallery();
    syncButton();
    cleanups.push(() => { changes.disconnect(); observer?.disconnect(); ctaObserver?.disconnect(); dots?.remove(); sticky.remove(); });
  }

  function initialize(scope = document) {
    const selector = '[data-oc-header], .oc-footer, .oc-main-product, .oc-faq, .oc-product-rail';
    [...controllers.entries()].forEach(([root, cleanup]) => { if (!root.isConnected) cleanup(); });
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      if (root.matches('.oc-main-product')) {
        [...controllers.entries()].forEach(([previous, cleanup]) => {
          if (previous !== root && previous.matches('.oc-main-product') && previous.dataset.section === root.dataset.section) cleanup();
        });
      }
      const events = new AbortController();
      const cleanups = [];
      controllers.set(root, () => { events.abort(); cleanups.forEach((fn) => fn()); controllers.delete(root); });
      setupAccordions(root, events);
      if (root.matches('.oc-product-rail')) setupRails(root, events);
      if (root.matches('[data-oc-header]')) setupHeader(root, events, cleanups);
      if (root.matches('.oc-main-product')) setupProduct(root, events, cleanups);
      if (root.matches('.oc-footer')) {
        const desktop = matchMedia('(min-width:992px)');
        const update = () => root.querySelectorAll('details.oc-footer__menu').forEach((menu) => { menu.open = desktop.matches; });
        desktop.addEventListener('change', update, { signal: events.signal });
        update();
      }
      if (root.matches('.oc-faq')) {
        root.querySelectorAll('.oc-faq__page-content details').forEach((item) => item.classList.add('oc-accordion'));
        const links = [...root.querySelectorAll('.oc-faq__nav a')];
        const update = () => {
          const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--oc-header-height')) + 80;
          let active = links[0];
          links.forEach((link) => { const target = document.getElementById(link.hash.slice(1)); if (target && target.getBoundingClientRect().top <= offset) active = link; });
          links.forEach((link) => { if (link === active) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
        };
        window.addEventListener('scroll', update, { signal: events.signal, passive: true });
        window.addEventListener('resize', update, { signal: events.signal });
        update();
      }
    });
    // Dawn owns cart requests, focus trapping, inventory, media and variant updates.
    scope.querySelector('cart-drawer')?.classList.add('oc-cart-drawer');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('product-info:loaded', (e) => initialize(e.target));
  document.addEventListener('shopify:section:load', (e) => initialize(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    [...controllers.entries()].forEach(([root, cleanup]) => { if (e.target.contains(root)) cleanup(); });
  });
})();
